// src/store/builders/useProfileStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const initialState = {
  isLoading: false,
  isUpdating: false,
  profile: null,
};

const useProfileStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      fetchProfile: async () => {
        set({ isLoading: true });

        const { token, user } = useAuthStore.getState();

        if (!token || !user || !user.id) {
          console.error("Builder authentication missing.");
          set({ isLoading: false, profile: null });
          return;
        }

        try {
          // Use explicit builder ID to avoid "profile" being treated as an ID parameter
          const response = await axiosInstancev1.get(
            `user-info/builder/${user.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const { builder: builderData } = response.data;

          if (!builderData) {
            set({ profile: null, isLoading: false });
            return;
          }

          // 🔥🔥 FIX: FLATTEN PROFILE OBJECT FOR FRONTEND
          const formattedProfile = {
            ...builderData,
            builder: builderData, // Keep for backward compatibility

            // Address details (already flattened in the builderData from backend based on the query)
            address_line1: builderData.address_line1 || "",
            address_line2: builderData.address_line2 || "",
            village: builderData.village || "",
            taluk: builderData.taluk || "",
            district: builderData.district || "",
            state: builderData.state || "Tamil Nadu",
            postcode: builderData.postcode || "",
            latitude: builderData.latitude || "",
            longitude: builderData.longitude || ""
          };

          set({
            profile: formattedProfile,
            isLoading: false
          });

        } catch (error) {
          set({ isLoading: false, profile: null });
          toast.error(error.response?.data?.message || "Failed to fetch builder profile");
        }
      },

      updateProfile: async (profileData) => {
        set({ isUpdating: true });

        const { token, user } = useAuthStore.getState();

        if (!token || !user || !user.id) {
          toast.error("Builder authentication missing for update.");
          set({ isUpdating: false });
          return false;
        }

        const builderId = user.id;

        const dataToSend = new FormData();
        for (const key in profileData) {
          const value = profileData[key];
          if (value !== undefined) {
            // Convert null to empty string to "clear" the field in the database
            dataToSend.append(key, value === null ? "" : value);
            // 🐛 DEBUG: Log each field being appended
            if (key === 'about') {
              console.log(`[useProfileStore] Appending ${key}:`, value?.substring(0, 100));
            }
          }
        }

        // 🐛 DEBUG: Log FormData contents
        console.log('[useProfileStore] FormData entries:');
        for (let pair of dataToSend.entries()) {
          if (pair[0] === 'about') {
            console.log(`  ${pair[0]}:`, pair[1]?.substring(0, 100));
          } else {
            console.log(`  ${pair[0]}:`, pair[1]);
          }
        }

        try {
          const response = await axiosInstancev1.put(
            `user-info/builder/${builderId}`,
            dataToSend,
            {
              headers: {
                'Content-Type': undefined, // Allow Axios to set correctly with boundary
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (response.data.success) {
            toast.success(response.data.message || "Profile updated successfully");

            // 🔥 FIX: Refresh profile to get updated data from server
            await get().fetchProfile();

            // 🔥 FIX: Also refresh dashboard card tile to show updated validity and profile image
            try {
              const { default: useDashboardStore } = await import('./useDashboardStore');
              await useDashboardStore.getState().getCardTile();
            } catch (err) {
              console.error('Failed to refresh dashboard card:', err);
            }

            set({ isUpdating: false });
            return { success: true };
          }

        } catch (error) {
          set({ isUpdating: false });
          const errorMessage = error.response?.data?.message || "Failed to update profile";
          toast.error(errorMessage);
          return { success: false, message: errorMessage };
        }

        return { success: false, message: "Unknown error occurred" };
      },

      reset: () => set(initialState),
    }),

    {
      name: "builder-profile-storage",
      getStorage: () => localStorage,
      partialize: () => ({}) // do NOT store profile locally
    }
  )
);

export default useProfileStore;
